/* ============================================================
   Add a business user — one flow for the three people who create them
   (Rodrigo, 2026-09-28):
     - Xhinola super admin and tenant admin pick the business;
     - a business admin never does (always their own business).

   Three steps, no progress bar:
     1. ID — scan it with the same UploadArea + DocumentScanner +
        analyzeDocument the pass flow uses (invitation/DocumentScanForm),
        or enter the details by hand. Captures what a pass's main guest
        captures: name, last name, date of birth, ID type, ID number,
        nationality, contact number.
     2. Sign-in — one "email or employee code" field that tells which one
        it is. Only email works today: POST /sam/users keys the Cognito user
        by email (samUserManagement create_user_handler); employee codes
        exist only for gatekeepers.
     3. Role — Admin (business_admin) or Front desk (business_invitation).

   Saving: POST /sam/users (name, family_name, email, phone, password,
   group, business_id), then — like CreateUserDrawer's estate link — find
   the DB user by cognitoid (the Cognito sub, see api/dbUserLookup) and
   PATCH /user/{id} with birthdate, idtype, idnum, nationality, which the
   create endpoint does not store.
   ============================================================ */
import { BUSINESS_USER_ROLES, DEFAULT_BUSINESS_ROLE } from '../utils/userAdmin';
import React, { useMemo, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Modal } from './ui/Modal';
import { toaster } from './ui/toast';
import { UploadArea } from './UploadArea';
import { DocumentScanner } from './DocumentScanner';
import { NationalityPicker } from './NationalityPicker';
import { FormattedPhoneInput } from './FormattedPhoneInput';
import { toIsoDate } from '../utils/dateUtils';
import {
  analyzeDocument, MAX_ANALYZER_IMAGE_BYTES, ACCEPTED_ANALYZER_IMAGE_MIME_TYPES,
} from '../services/documentAnalysis';
import { useCreateUser } from '../hooks/useSAMUsers';
import { useBusinessPositions, useCreateBusinessEmployee } from '../hooks/useBusinessEmployees';
import type { CreateEmployeeRequest } from '../api/employeesApi';
import { useTenantIds } from '../hooks/useTenant';
import { toNationalityIsoCode, useNationalities } from '../hooks/useNationalities';
import { findDbUserIdForCreatedUser } from '../api/dbUserLookup';
import { patchUserId } from '../api/generated';
import type { Business } from '../hooks/useBusinessAccessType';
import { DateField } from './ui/DateTimeField';

type PhoneCountry = React.ComponentProps<typeof FormattedPhoneInput>['country'];
const DEFAULT_PHONE_COUNTRY = { label: 'Dominican Republic', id: 'DO', dialCode: '+1' } as PhoneCountry;

type IdType = 'documentId' | 'passport' | 'driverLicense';
const EMPLOYEE_ID_TYPE: Record<IdType, NonNullable<CreateEmployeeRequest['id_type']>> = {
  documentId: 'cedula', passport: 'passport', driverLicense: 'driver_license',
};
const ID_TYPES: Array<{ id: IdType; label: string }> = [
  { id: 'documentId', label: 'Document ID' },
  { id: 'passport', label: 'Passport' },
  { id: 'driverLicense', label: 'Driver License' },
];

export { BUSINESS_USER_ROLES };

interface Person {
  first: string;
  last: string;
  dob: string;
  idType: IdType;
  idNumber: string;
  nationality: string;
  phone: string;
}

const EMPTY_PERSON: Person = {
  first: '', last: '', dob: '', idType: 'documentId', idNumber: '', nationality: '', phone: '',
};

const LINK: React.CSSProperties = { color: 'var(--tenant-primary)', border: 'none', background: 'none', padding: 0 };

export type LoginKind = 'none' | 'email' | 'code';
/** "Smart" sign-in field: an @ means email, anything else is an employee code. */
export function loginKind(value: string): LoginKind {
  const v = value.trim();
  if (!v) return 'none';
  return v.includes('@') ? 'email' : 'code';
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** The business being viewed (Team tab) — preselected, or the only option for a business admin. */
  businessId?: string | null;
  /** Businesses the creator can assign. More than one => the creator picks (super / tenant admin). */
  businesses: Business[];
  /** Settings → Admin users puts the business first; the Team tab puts it with the role. */
  businessFirst?: boolean;
  /**
   * The tenant Front Desk adds an employee, not a sign-in account (SAM-1297): the ID, then position and end date.
   * POST /businesses/{b}/employees: no login, no gate zones, no app access; Security reviews it before it counts.
   */
  employee?: boolean;
}

export const AddBusinessUserSheet: React.FC<Props> = ({
  isOpen, onClose, businessId, businesses, businessFirst = false, employee = false,
}) => {
  const { createUserAsync, isCreating: creatingUser } = useCreateUser();
  const { mutateAsync: createEmployeeAsync, isPending: creatingEmployee } = useCreateBusinessEmployee(businessId);
  const isCreating = creatingUser || creatingEmployee;
  const { data: positions = [], isLoading: loadingPositions } = useBusinessPositions(businessId, employee && isOpen);
  const { currentTenantId } = useTenantIds();
  const { data: countries } = useNationalities();

  const sortedBusinesses = useMemo(
    () => [...businesses].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [businesses],
  );
  // Opened from a business (its Team tab) the business is fixed; the picker is only for the sheet opened with
  // no business in context (Settings → Admin users), where a tenant admin chooses one.
  const picksBusiness = !businessId && sortedBusinesses.length > 1;

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [idMode, setIdMode] = useState<'none' | 'scan' | 'manual'>('none');
  const [scanStatus, setScanStatus] = useState<'loading' | 'success' | 'error' | null>(null);
  const [scanPreview, setScanPreview] = useState('');
  const [scanError, setScanError] = useState('');
  // The analyzer already saved the photo in S3 and answers with its keys; the user row keeps them (as the user
  // page does after a document upload: photo_url = the ID, profile_picture = the face crop).
  const [scanKeys, setScanKeys] = useState<{ photo?: string; profile?: string }>({});
  const [person, setPerson] = useState<Person>(EMPTY_PERSON);
  const [phoneCountry, setPhoneCountry] = useState<PhoneCountry>(DEFAULT_PHONE_COUNTRY);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState<string>(DEFAULT_BUSINESS_ROLE);
  const [targetBusiness, setTargetBusiness] = useState<string>(businessId || '');
  const [positionKey, setPositionKey] = useState('');
  const [validUntil, setValidUntil] = useState('');

  const set = (k: keyof Person, v: string) => setPerson((p) => ({ ...p, [k]: v }));
  const kind = loginKind(login);

  const reset = () => {
    setStep(1); setIdMode('none'); setScanStatus(null); setScanPreview(''); setScanError(''); setScanKeys({});
    setPerson(EMPTY_PERSON); setPhoneCountry(DEFAULT_PHONE_COUNTRY); setLogin(''); setPassword('');
    setRole(DEFAULT_BUSINESS_ROLE); setTargetBusiness(businessId || '');
    setPositionKey(''); setValidUntil('');
  };
  const close = () => { if (isCreating) return; reset(); onClose(); };

  // --- Step 1: scan (same service and limits as the pass flow) ---
  const onScanFile = (file: File) => {
    setScanError('');
    if (file.size > MAX_ANALYZER_IMAGE_BYTES) { setScanError('Image is too large. Use a photo under 7 MB.'); return; }
    if (file.type && !ACCEPTED_ANALYZER_IMAGE_MIME_TYPES.includes(file.type)) {
      setScanError('Unsupported file format. Please upload a JPG or PNG image.'); return;
    }
    const reader = new FileReader();
    reader.onloadend = async () => {
      const dataUrl = reader.result as string;
      setScanPreview(dataUrl);
      setScanStatus('loading');
      setScanKeys({});
      try {
        const res = await analyzeDocument({ image: dataUrl.split(',')[1], filename: file.name });
        const d = (res?.data || {}) as unknown as Record<string, string>;
        if (!d.number && !d.name && !d.LastName) {
          setScanStatus('error');
          setScanError("We couldn't recognize this document. Try a clearer photo or enter the details manually.");
          return;
        }
        setPerson((p) => ({
          ...p,
          first: d.name || p.first,
          last: d.LastName || p.last,
          dob: toIsoDate(d.DOB) || p.dob,
          idType: (res.documentType as IdType) || p.idType,
          idNumber: d.number || p.idNumber,
          nationality: d.nationality || p.nationality,
        }));
        setScanKeys({ photo: d.picture || undefined, profile: d.profile_picture || undefined });
        setScanStatus('success');
        setIdMode('scan');
      } catch {
        setScanStatus('error');
        setScanError("We couldn't analyze this document. Check your connection and try again, or enter the details manually.");
      } finally {
        window.setTimeout(() => setScanStatus(null), 1400);
      }
    };
    reader.readAsDataURL(file);
  };

  const lastStep: 2 | 3 = employee ? 2 : 3;
  // The employee route needs a contact number, a position and an end date.
  const step1Valid = idMode !== 'none' && person.first.trim() !== '' && person.last.trim() !== '' && (!employee || person.phone.trim() !== '');
  const step2Valid = employee
    ? !!positionKey && !!validUntil
    : kind === 'email' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(login.trim()) && password.length >= 8;
  const step3Valid = !!role && (!!targetBusiness || !picksBusiness);
  const canNext = step === 1 ? step1Valid : step === 2 ? step2Valid : step3Valid;

  const saveEmployee = async () => {
    const body: CreateEmployeeRequest = {
      first_name: person.first.trim(),
      last_name: person.last.trim(),
      phone: person.phone,
      position_key: positionKey,
      valid_until: validUntil,
      birthdate: person.dob || undefined,
      nationality: toNationalityIsoCode(person.nationality, countries) ?? (person.nationality || undefined),
      id_type: person.idNumber ? EMPLOYEE_ID_TYPE[person.idType] : undefined,
      id_number: person.idNumber.trim() || undefined,
    };
    try {
      await createEmployeeAsync(body);
      toaster.positive(`${body.first_name} ${body.last_name} added. Security reviews them before they get access.`, { autoHideDuration: 6000 });
      close();
    } catch {
      // useCreateBusinessEmployee already shows the error toast
    }
  };

  const save = async () => {
    const business = businessId || targetBusiness || '';
    const email = login.trim().toLowerCase();
    try {
      const result = await createUserAsync({
        email,
        password,
        name: person.first.trim(),
        family_name: person.last.trim(),
        phone_number: person.phone || undefined,
        group: role,
        business_id: business || undefined,
        tenant_id: currentTenantId || undefined,
        custom_attributes: { tenant_id: currentTenantId || undefined, business_id: business || undefined },
      });
      const cognitoUsername = result?.user?.username;
      close();
      if (cognitoUsername) saveIdentityInBackground(cognitoUsername, email, result?.user_id);
    } catch {
      // useCreateUser already shows the error toast
    }
  };

  // The create endpoint doesn't store the ID fields; write them to the DB row once it exists.
  const saveIdentityInBackground = async (cognitoUsername: string, email: string, knownUserId?: number | null) => {
    const identity = {
      birthdate: person.dob || undefined,
      idtype: person.idNumber ? person.idType : undefined,
      idnum: person.idNumber || undefined,
      // Stored as the ISO code the picker uses (an OCR'd "MARRUECOS" becomes MA); unknown text is kept as read.
      nationality: toNationalityIsoCode(person.nationality, countries) ?? (person.nationality || undefined),
      // The scanned ID, so the member's page can show it (View ID). Empty when the details were typed.
      photo_url: scanKeys.photo,
      profile_picture: scanKeys.profile,
    };
    if (!Object.values(identity).some(Boolean)) return;
    const dbUserId = await findDbUserIdForCreatedUser(cognitoUsername, email, { knownUserId });
    if (!dbUserId) {
      toaster.warning('User created — add their ID details from the user page.', { autoHideDuration: 8000 });
      return;
    }
    try {
      await patchUserId(String(dbUserId), identity as never);
    } catch {
      toaster.warning('User created — their ID details could not be saved. Add them from the user page.', { autoHideDuration: 8000 });
    }
  };

  const businessField = picksBusiness ? (
    <div className="fc" style={{ marginBottom: 14 }}>
      <label htmlFor="abu-business">Business <span className="req">*</span></label>
      <select id="abu-business" value={targetBusiness} onChange={(e) => setTargetBusiness(e.target.value)}>
        <option value="">Select a business</option>
        {sortedBusinesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
    </div>
  ) : null;

  const footer = (
    <div className="flex items-center justify-between w-full gap-2">
      <button type="button" className="btn ghost" onClick={() => (step === 1 ? close() : setStep((s) => (s - 1) as 1 | 2 | 3))} disabled={isCreating}>
        {step === 1 ? 'Cancel' : 'Back'}
      </button>
      <button
        type="button"
        className="btn primary"
        disabled={!canNext || isCreating}
        onClick={() => (step < lastStep ? setStep((s) => (s + 1) as 1 | 2 | 3) : employee ? saveEmployee() : save())}
      >
        {step < lastStep ? 'Next' : isCreating ? 'Creating…' : employee ? 'Add member' : 'Create user'}
      </button>
    </div>
  );

  const field = (label: string, key: keyof Person, placeholder: string) => (
    <div className="fc" style={{ marginBottom: 12 }}>
      <label htmlFor={`abu-${key}`}>{label}</label>
      <input id={`abu-${key}`} value={person[key]} placeholder={placeholder} onChange={(e) => set(key, e.target.value)} />
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      isDrawer
      title="Add team member"
      subtitle={step === 1 ? 'Their ID' : step === 2 ? (employee ? 'Position and end date' : 'How they sign in') : 'What they can do'}
      footer={footer}
    >
      {scanStatus && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 20000 }}>
          <DocumentScanner status={scanStatus} size="md" color="var(--tenant-primary)" />
        </div>
      )}

      {step === 1 && (
        <>
          {businessFirst && businessField}
          {idMode === 'none' ? (
            <>
              <UploadArea
                onFileSelect={onScanFile}
                accept=".png,.jpg,.jpeg"
                title="ID document"
                hint="Drag & drop or click to upload"
                color="var(--tenant-primary)"
              />
              <div className="flex justify-between mt-2 text-xs" style={{ color: 'var(--ink-400)' }}>
                <span>Accepted formats: JPG, PNG</span>
                <button type="button" className="underline font-medium" style={LINK} onClick={() => setIdMode('manual')}>
                  Enter details manually
                </button>
              </div>
              {scanError && <div className="notice notice-error mt-3">{scanError}</div>}
            </>
          ) : (
            <>
              {idMode === 'scan' && scanPreview && (
                <img src={scanPreview} alt="Scanned document" style={{ width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 8, marginBottom: 12 }} />
              )}
              <div className="grid grid-cols-2 gap-x-3">
                {field('First Name', 'first', 'First Name')}
                {field('Last Name', 'last', 'Last Name')}
                <div className="fc" style={{ marginBottom: 12 }}>
                  <DateField
                    label="Date of birth" ariaLabel="Date of birth" value={person.dob}
                    max={new Date().toISOString().slice(0, 10)} onChange={(v) => set('dob', v)}
                  />
                </div>
                <div className="fc" style={{ marginBottom: 12 }}>
                  <label htmlFor="abu-idtype">ID type</label>
                  <select id="abu-idtype" value={person.idType} onChange={(e) => set('idType', e.target.value)}>
                    {ID_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                {field('ID Number', 'idNumber', 'ID Number')}
                <div style={{ marginBottom: 12 }}>
                  <div className="text-[13px] font-semibold mb-1.5" style={{ color: 'var(--ink-700)' }}>Nationality</div>
                  <NationalityPicker value={person.nationality} onChange={(code) => set('nationality', code)} size="compact" />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div className="text-[13px] font-semibold mb-1.5" style={{ color: 'var(--ink-700)' }}>Contact number</div>
                <FormattedPhoneInput
                  value={person.phone}
                  country={phoneCountry}
                  onChange={(value, country) => { set('phone', value); setPhoneCountry(country); }}
                  placeholder="Enter contact number"
                />
              </div>
              <button
                type="button"
                className="text-xs underline font-medium"
                style={LINK}
                onClick={() => { setIdMode('none'); setScanPreview(''); setScanKeys({}); }}
              >
                {idMode === 'scan' ? 'Scan again' : 'Scan an ID instead'}
              </button>
            </>
          )}
        </>
      )}

      {step === 2 && employee && (
        <>
          <div className="fc" style={{ marginBottom: 12 }}>
            <label htmlFor="abu-position">Position <span className="req">*</span></label>
            <select id="abu-position" value={positionKey} onChange={(e) => setPositionKey(e.target.value)} disabled={loadingPositions}>
              <option value="">{loadingPositions ? 'Loading…' : 'Select a position'}</option>
              {positions.map((p) => <option key={p.key} value={p.key}>{p.label_en}</option>)}
            </select>
          </div>
          <div className="fc" style={{ marginBottom: 6 }}>
            <DateField
              label="Valid until" ariaLabel="Valid until" value={validUntil}
              min={new Date().toISOString().slice(0, 10)} onChange={setValidUntil}
            />
          </div>
          <p className="text-xs" style={{ color: 'var(--ink-500)' }}>
            Security reviews every new team member before they get access. Gate zones and app access are set there.
          </p>
        </>
      )}

      {step === 2 && !employee && (
        <>
          <div className="fc" style={{ marginBottom: 6 }}>
            <label htmlFor="abu-login">Email or employee code</label>
            <input id="abu-login" value={login} onChange={(e) => setLogin(e.target.value)} placeholder="name@business.com or AB-1042" />
          </div>
          <p className="text-xs mb-4" style={{ color: kind === 'code' ? 'var(--warn-fg)' : 'var(--ink-500)' }}>
            {kind === 'none' && 'We detect whether it’s an email or an employee code.'}
            {kind === 'email' && 'Email · they sign in with it.'}
            {kind === 'code' && 'Employee code · not available for business users yet. Use an email for now.'}
          </p>
          <div className="fc">
            <label htmlFor="abu-password">Password</label>
            <div style={{ position: 'relative' }}>
              <input id="abu-password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" style={{ paddingRight: 40 }} />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 0, cursor: 'pointer', color: 'var(--ink-500)' }}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div className="cap">Share it with them in person; they can change it after signing in.</div>
          </div>
        </>
      )}

      {step === 3 && (
        <>
          {!businessFirst && businessField}
          <div role="radiogroup" aria-label="Role" className="flex flex-col gap-2">
            {BUSINESS_USER_ROLES.map((r) => {
              const on = role === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  onClick={() => setRole(r.id)}
                  className="text-left px-3.5 py-3"
                  style={{
                    borderRadius: 'var(--r-8)',
                    border: on ? '2px solid var(--tenant-primary)' : '1px solid var(--line)',
                    background: on ? 'color-mix(in srgb, var(--tenant-primary) 8%, white)' : '#fff',
                  }}
                >
                  <div className="text-[14px] font-semibold" style={{ color: 'var(--ink-900)' }}>{r.name}</div>
                  <div className="text-[12px]" style={{ color: 'var(--ink-500)' }}>{r.desc}</div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </Modal>
  );
};

export default AddBusinessUserSheet;
